import { Box, Button, Group, Image, Loader, Modal, Stack, Text, Title } from "@mantine/core";
import { useDocumentStore } from "@pages/LandingPage/DocumentStore.ts";
import GlowButton from "@widgets/PortalLayout/components/GlowButton/GlowButton.tsx";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import snarkdown from "snarkdown";

export const DocumentModal = () => {
	const { t } = useTranslation();
	const { isOpen, currentDocumentUrl, error, closeDocument, documentTitle, documentType } = useDocumentStore();
	const [iframeError, setIframeError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [markdownContent, setMarkdownContent] = useState<string>("");
	const [imageError, setImageError] = useState(false);
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [contentReady, setContentReady] = useState(false);

	// Reset states when modal opens/closes or URL changes
	useEffect(() => {
		if (isOpen && currentDocumentUrl) {
			setIframeError(false);
			setImageError(false);
			setIsLoading(true);
			setMarkdownContent("");
			setContentReady(false);
		}
	}, [isOpen, currentDocumentUrl, documentType]);

	useEffect(() => {
		if (!isLoading) {
			const timer = setTimeout(() => setContentReady(true), 50);
			return () => clearTimeout(timer);
		}
	}, [isLoading]);

	// Fetch markdown content
	useEffect(() => {
		if (documentType === "markdown" && currentDocumentUrl) {
			setMarkdownContent(currentDocumentUrl);
			setIsLoading(false);

		}
	}, [documentType, currentDocumentUrl]);

	// Check if iframe can access its content (detect X-Frame-Options blocks)
	useEffect(() => {
		if (documentType !== "iframe" || !isLoading || !iframeRef.current) return;

		const checkIframeAccess = () => {
			try {
				const iframe = iframeRef.current;
				if (!iframe) return;

				// Try to access the iframe's document
				const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

				// If we can't access the document, it might be blocked
				if (!iframeDoc) {
					// Wait a bit more and try again
					setTimeout(() => {
						try {
							const doc = iframe.contentDocument || iframe.contentWindow?.document;
							if (!doc) {
								setIsLoading(false);
								setIframeError(true);
							}
						} catch (e) {
							setIsLoading(false);
							setIframeError(true);
						}
					}, 3000);
					return;
				}

				// Check if the document is actually loaded with content
				if (iframeDoc.readyState === "complete") {
					// Check if it's an error page or blocked
					const title = iframeDoc.title?.toLowerCase() || "";
					const body = iframeDoc.body?.textContent?.toLowerCase() || "";

					if (
						title.includes("refused") ||
						title.includes("denied") ||
						body.includes("refused to connect") ||
						body.includes("x-frame-options")
					) {
						setIsLoading(false);
						setIframeError(true);
					}
				}
			} catch (e) {
				// Cross-origin access denied - likely X-Frame-Options block
				setIsLoading(false);
				setIframeError(true);
			}
		};

		// Check immediately and after a delay
		const timeoutId = setTimeout(checkIframeAccess, 2000);

		return () => clearTimeout(timeoutId);
	}, [isLoading, documentType]);

	const handlePrint = async () => {
		// For image type, handle download instead of print
		if (documentType === "image" && currentDocumentUrl) {
			try {
				// Fetch the image data as a blob
				const response = await fetch(currentDocumentUrl);
				if (!response.ok) {
					throw new Error("Network response was not OK");
				}
				const blob = await response.blob();

				// Create a temporary URL for the blob
				const url = window.URL.createObjectURL(blob);

				// Create a link element to trigger the download
				const link = document.createElement("a");
				link.href = url;
				const filename = documentTitle || currentDocumentUrl.split("/").pop() || "download";
				link.setAttribute("download", filename);

				// Append, click, and remove the link
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				// Clean up the object URL
				window.URL.revokeObjectURL(url);
			} catch (error) {
				// Fallback for CORS issues or other errors
				alert(
					"Could not download the image directly due to security restrictions. The image will open in a new tab. Please right-click it and select 'Save Image As...' to download."
				);
				window.open(currentDocumentUrl, "_blank", "noopener,noreferrer");
			}
			return;
		}

		// Keep original print functionality for other document types
		if (documentType === "iframe") {
			const iframe = iframeRef.current;
			if (iframe) {
				try {
					const iframeWindow = iframe.contentWindow;
					if (iframeWindow) {
						iframeWindow.focus();
						iframeWindow.print();
					}
				} catch (e) {
					// Fallback: try to print the parent window
					window.print();
				}
			}
		} else {
			// For markdown, print the current window
			window.print();
		}
	};

	const handleOpenInNewPage = () => {
		if (currentDocumentUrl) {
			window.open(currentDocumentUrl, "_blank", "noopener,noreferrer");
		}
	};

	const handleIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
		const iframe = e.currentTarget;

		try {
			// Try to access the iframe content to verify it loaded properly
			const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

			if (iframeDoc) {
				// Check if it's actually the content we want or an error page
				const title = iframeDoc.title?.toLowerCase() || "";
				const body = iframeDoc.body?.textContent?.toLowerCase() || "";

				if (
					title.includes("refused") ||
					title.includes("denied") ||
					body.includes("refused to connect") ||
					body.includes("x-frame-options") ||
					body.includes("this site cant be reached")
				) {
					setIsLoading(false);
					setIframeError(true);
					return;
				}
			}

			// Successfully loaded
			setIsLoading(false);
			setIframeError(false);
			iframe.style.display = "block";
		} catch (e) {
			// Cross-origin restriction - treat as error
			setIsLoading(false);
			setIframeError(true);
		}
	};

	const handleIframeError = () => {
		setIsLoading(false);
		setIframeError(true);
	};

	const handleImageLoad = () => {
		setIsLoading(false);
		setImageError(false);
	};

	const handleImageError = () => {
		setIsLoading(false);
		setImageError(true);
	};

	const renderContent = () => {
		if (!currentDocumentUrl) return null;

		switch (documentType) {
			case "markdown":
				return (
					<React.Fragment>
						{/* Show loader while markdown is loading */}
						{isLoading && !iframeError && (
							<Box
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									padding: "40px 20px",
									minHeight: "200px"
								}}
							>
								<Loader size="xl" mb="md" />
								<Text size="sm" c="dimmed">
									{t("document.loading", "Loading document...")}
								</Text>
							</Box>
						)}

						{/* Show error message and open button if markdown fails */}
						{iframeError && (
							<Box style={{ textAlign: "center", padding: "80px 20px" }}>
								<GlowButton onClick={handleOpenInNewPage} variant="filled" size="xl">
									{t("document.open_in_new_page", `Open ${documentTitle}`)}
								</GlowButton>
							</Box>
						)}

						{/* Show markdown content */}
						{ markdownContent && (
							<Box

								dangerouslySetInnerHTML={{ __html: snarkdown(markdownContent) }}
							/>

						)}
					</React.Fragment>
				);

			case "image":
				return (
					<React.Fragment>
						{/* Show loader while image is loading */}
						{isLoading && !imageError && (
							<Box
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									padding: "40px 20px",
									minHeight: "200px"
								}}
							>
								<Loader size="xl" mb="md" />
								<Text size="sm" c="dimmed">
									{t("document.loading", "Loading...")}
								</Text>
							</Box>
						)}

						{/* Show error message and open button if image fails */}
						{imageError && (
							<Box style={{ textAlign: "center", padding: "80px 20px" }}>
								<GlowButton onClick={handleOpenInNewPage} variant="filled" size="xl">
									{t("document.open_in_new_page", `Open ${documentTitle}`)}
								</GlowButton>
							</Box>
						)}

						{/* Show image */}
						{!imageError && (
							<Box style={{ textAlign: "center" }}>
								<Image
									src={currentDocumentUrl}
									alt={documentTitle || "Document"}
									style={{
										maxWidth: "100%",
										maxHeight: "60vh",
										objectFit: "contain",
										display: isLoading ? "none" : "block",
										opacity: contentReady ? 1 : 0,
										transition: "opacity 300ms ease-in-out"
									}}
									onLoad={handleImageLoad}
									onError={handleImageError}
								/>
							</Box>
						)}
					</React.Fragment>
				);

			case "iframe":
			default:
				return (
					<React.Fragment>
						{/* Show loader while iframe is loading */}
						{isLoading && !iframeError && (
							<Box
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									padding: "40px 20px",
									minHeight: "200px"
								}}
							>
								<Loader size="xl" mb="md" />
								<Text size="sm" c="dimmed">
									{t("document.loading", "Loading document...")}
								</Text>
							</Box>
						)}


						{/* Show error message and open button if iframe fails */}
						{iframeError && (
							<Box style={{ textAlign: "center", padding: "80px 20px" }}>
								<GlowButton onClick={handleOpenInNewPage} variant="filled" size="xl">
									{t("document.open_in_new_page", `Open ${documentTitle}`)}
								</GlowButton>
							</Box>
						)}

						{/* Only show iframe if there's no error */}
						{!iframeError && (
							<iframe
								ref={iframeRef}
								src={currentDocumentUrl}
								style={{
									width: "100%",
									height: "60vh",
									border: "none",
									display: isLoading ? "none" : "block",
									opacity: contentReady ? 1 : 0,
									transition: "opacity 300ms ease-in-out"
								}}
								onLoad={handleIframeLoad}
								onError={handleIframeError}
								title="Document Viewer"
							/>
						)}
					</React.Fragment>
				);
		}
	};

	if (error) {
		return <Text c="red">{error}</Text>;
	}

	//if (!currentDocumentUrl) return null;

	const hasError = iframeError || imageError;

	return (
		<Modal
			opened={!!currentDocumentUrl && isOpen}
			onClose={closeDocument}
			title={documentTitle}
			size="xl"
			centered
			transitionProps={{ transition: 'slide-down' }}
			styles={{
				body: { maxHeight: "70vh", overflowY: "auto" }
			}}
		>
			<Stack style={{ minHeight: "200px" }}>
				<Box>{renderContent()}</Box>

				<Group justify="flex-end">
					{!hasError && (
						<>
							<Button onClick={handlePrint} variant="outline">
								{documentType === "image" 
									? t("common.download", "Download") 
									: t("common.print", "Print")}
							</Button>

							<Button onClick={closeDocument}>{t("common.close", "Close")}</Button>
						</>
					)}
				</Group>
			</Stack>
		</Modal>
	);
};
